import { useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Select } from '@components/select';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { sortByObjectKey } from '@utils/collections';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

type Props = ModalProps<{
  applicationId: number;
  applicationName: string;
  applicationAssignedToId: number | null;
}>;

export const AssignUserToApplicantModal = (props: Props) => {
  const { applicationId, applicationName, applicationAssignedToId, closeModal } = props;
  const { userStore, applicationStore, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const [assignedTo, setAssignedTo] = useState<string | null>(
    applicationAssignedToId?.toString() || null,
  );

  const queryClient = useQueryClient();
  const followersQuery = useQuery({
    queryKey: ['application', 'followers', applicationId],
    queryFn: () => api.applications.followers(applicationId),
  });

  const submit = useMutation(
    () => api.applications.setAssignedTo(applicationId, assignedTo || null),
    {
      onSuccess() {
        queryClient.invalidateQueries(['messages']);
        // Update store state for those not using react-query
        const application = applicationStore.items.get(String(applicationId));
        if (assignedTo) {
          const assignedUser = userStore.items.get(assignedTo);
          application?.setAssignedTo(assignedUser);
          showSuccess(t('application.setassignedto.success', applicationName, assignedUser.name));
        } else {
          application?.setAssignedTo(null);
          showSuccess(t('application.setassignedto.remove', applicationName));
        }
        closeModal();
      },
      onError() {
        showError(
          t(
            'application.setassignedto.error',
            applicationName,
            userStore.items.get(assignedTo).name,
          ),
        );
      },
    },
  );

  const followers = useMemo(() => {
    const users: IUser[] = userStore.list.filter((user: IUser) =>
      (followersQuery.data ?? []).includes(user.id),
    );
    return sortByObjectKey(users, 'name').map((follower) => ({
      value: follower.id.toString(),
      label: follower.name,
      helpText: follower.roleDisplay,
    }));
  }, [userStore.list, followersQuery.data]);

  if (followersQuery.isLoading) {
    return <Loading />;
  }

  const isRemoving = applicationAssignedToId && !assignedTo;

  return (
    <Modal>
      <Modal.Header text={t('assignmodal.header')} />
      <Text text={t('assignmodal.description')} />
      <Select
        defaultValue={applicationAssignedToId?.toString() || undefined}
        onChange={setAssignedTo}
        placeholder={t('assignmodal.select.emptystate')}
        label={t('assignmodal.select.label')}
        options={followers}
      />

      <Modal.Actions>
        <Button
          isDisabled={submit.isLoading}
          variant="secondary"
          onClick={closeModal}
          text={t('assignmodal.button.cancel')}
        />
        <Button
          isDisabled={
            submit.isLoading ||
            (!applicationAssignedToId && !assignedTo) ||
            applicationAssignedToId?.toString() === assignedTo
          }
          onClick={() => submit.mutate()}
          text={isRemoving ? t('common.forms.button.save') : t('assignmodal.button.submit')}
          variant={isRemoving ? 'primary-blue' : 'primary'}
        />
      </Modal.Actions>
    </Modal>
  );
};
